import type { ActionItem } from '@cartwheel/api/v1'
import { Badge, Button, Modal, Textarea } from '@cartwheel/components'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { formatCategory, getBadgeColor } from '../utils/action-item-helpers'

const CONTACT_INFO_CATEGORIES = new Set([
  'first_outreach_pending',
  'outreach_ongoing',
  'cost_under_family_review',
  'self_schedule_pending',
])

function renderSuggestedAction(text: string): ReactNode {
  const paragraphs = text.split(/\n{2,}/)

  const blocks = paragraphs.map((paragraph, pIndex) => {
    const lines = paragraph.split('\n').filter((line) => line.trim() !== '')
    const bulletLines = lines.filter((line) => /^-\s/.test(line.trim()))
    const numberedLines = lines.filter((line) => /^\d+\.\s/.test(line.trim()))
    const textLines = lines.filter(
      (line) => !/^-\s/.test(line.trim()) && !/^\d+\.\s/.test(line.trim())
    )

    if (numberedLines.length > 0 && bulletLines.length === 0) {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
        <ol key={pIndex} className="list-decimal list-inside space-y-1 text-sm text-charcoal">
          {numberedLines.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
            <li key={i}>{line.replace(/^\d+\.\s/, '').trim()}</li>
          ))}
        </ol>
      )
    }

    if (bulletLines.length > 0) {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
        <div key={pIndex} className="space-y-1">
          {textLines.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
            <p key={i} className="text-sm text-charcoal">
              {line.trim()}
            </p>
          ))}
          <ul className="list-disc list-inside space-y-1 text-sm text-charcoal">
            {bulletLines.map((line, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
              <li key={i}>{line.replace(/^-\s/, '').trim()}</li>
            ))}
          </ul>
        </div>
      )
    }

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: static list from formatted text
      <p key={pIndex} className="text-sm text-charcoal">
        {lines.join(' ')}
      </p>
    )
  })

  return blocks.length === 1 ? blocks[0] : blocks
}

export interface ActionItemModalProps {
  isOpen: boolean
  onClose: () => void
  actionItem: ActionItem | null
  onMarkCompleted: (note: string) => void
  onSendNote: (note: string) => void
  isPending?: boolean
  isLoading?: boolean
  errorMessage?: string | null
}

export function ActionItemModal({
  isOpen,
  onClose,
  actionItem,
  onMarkCompleted,
  onSendNote,
  isPending = false,
  isLoading = false,
  errorMessage = null,
}: ActionItemModalProps) {
  const [note, setNote] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const handleMarkCompleted = () => {
    if (!actionItem) return

    onMarkCompleted(note)
    setNote('')
  }

  const handleSendNote = () => {
    if (!actionItem) return

    onSendNote(note)
    setNote('')
  }

  const handleClose = () => {
    setNote('')
    setLinkCopied(false)
    onClose()
  }

  if (isLoading || (!actionItem && !errorMessage)) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Take action">
        <div className="space-y-6">
          <p className="text-sm text-charcoal-75">Loading action item details...</p>

          <div className="flex justify-end pt-4 border-t border-gray-stroke">
            <Button variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (!actionItem) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Take action">
        <div className="space-y-6">
          <p className="text-sm text-charcoal-75">
            {errorMessage || 'Unable to load action item details.'}
          </p>

          <div className="flex justify-end pt-4 border-t border-gray-stroke">
            <Button variant="secondary" onClick={handleClose} disabled={isPending}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const isMissingEmailOutreach =
    actionItem.category === 'outreach_ongoing' &&
    (actionItem.dynamic_data as Record<string, string>)?.reason === 'missing_email'

  const showContactInfo =
    CONTACT_INFO_CATEGORIES.has(actionItem.category) &&
    !!actionItem.primary_contact &&
    !isMissingEmailOutreach

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <a
          href={`/students/${actionItem.client_id}`}
          className="text-forest underline hover:text-forest-dark"
        >
          {actionItem.student_name}
        </a>
      }
    >
      <div className="space-y-4">
        {/* Service type subtitle */}
        {actionItem.service_type && (
          <p className="text-charcoal-75 text-sm -mt-4">{actionItem.service_type}</p>
        )}

        {/* Client status badge and issue description */}
        <div className="pb-4 border-b border-gray-stroke">
          <Badge
            label={formatCategory(actionItem.client_status ?? actionItem.category)}
            color={getBadgeColor(actionItem.client_status ?? actionItem.category)}
          />
          {actionItem.issue_description && (
            <p className="text-sm text-charcoal-75 mt-2">{actionItem.issue_description}</p>
          )}
        </div>

        {/* Recommended Next Steps card */}
        {actionItem.suggested_action && (
          <div className="bg-sand-75 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-charcoal">Recommended Next Steps</h3>
            <div>{renderSuggestedAction(actionItem.suggested_action)}</div>

            {actionItem.category === 'care_extension_review_pending' &&
              (() => {
                const sessionLimitId = (actionItem.dynamic_data as Record<string, string> | null)
                  ?.session_limit_id
                if (!sessionLimitId) return null
                return (
                  <a
                    href={`/v2/reauthorization/${sessionLimitId}/review`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-forest underline hover:text-forest-dark"
                  >
                    <i className="fas fa-arrow-up-right-from-square text-sm" />
                    Review care extension
                  </a>
                )
              })()}

            {actionItem.suggested_action_link && (
              <div className="bg-white rounded-lg px-4 py-2 space-y-2">
                <p className="text-sm font-semibold text-charcoal">
                  {actionItem.suggested_action_link.label}
                </p>
                <div className="flex items-center border border-gray-stroke rounded-lg pl-4 py-2 overflow-hidden">
                  <div className="flex flex-1 items-center gap-2 min-w-0 overflow-hidden">
                    <i className="fas fa-link text-charcoal text-sm shrink-0" />
                    <span className="text-sm text-charcoal-50 truncate">
                      {actionItem.suggested_action_link.url}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    className="h-7 px-2 py-0 text-sm gap-1 shrink-0 border-transparent hover:border-transparent"
                    onClick={() => handleCopyLink(actionItem.suggested_action_link?.url ?? '')}
                  >
                    <i className={`fas ${linkCopied ? 'fa-check' : 'fa-copy'} text-sm`} />
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Family Contact Information card */}
        {showContactInfo && (
          <div className="bg-white-dark rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-charcoal">Family Contact Information</h3>
            <p className="text-sm text-charcoal">{actionItem.primary_contact?.name}</p>
            {actionItem.primary_contact?.phone && (
              <div className="flex items-center gap-2 text-sm text-charcoal">
                <i className="fas fa-phone w-4" />
                <span>{actionItem.primary_contact?.phone}</span>
              </div>
            )}
            {actionItem.primary_contact?.email && (
              <div className="flex items-center gap-2 text-sm text-charcoal">
                <i className="fas fa-envelope w-4" />
                <span>{actionItem.primary_contact?.email}</span>
              </div>
            )}
          </div>
        )}

        {/* Notes to Cartwheel team */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-charcoal">Notes to Cartwheel team</h3>
            <span className="text-sm text-charcoal-75">(optional)</span>
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Share additional context, challenges, or questions for the Cartwheel team."
            rows={4}
            disabled={isPending}
          />
        </div>

        {/* Action buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-stroke">
          <Button variant="secondary" onClick={handleMarkCompleted} disabled={isPending}>
            <i className="fas fa-circle-check w-4 mr-1" />
            Mark as complete
          </Button>
          <Button variant="primary" onClick={handleSendNote} disabled={!note.trim() || isPending}>
            Send note to Cartwheel
          </Button>
        </div>
      </div>
    </Modal>
  )
}
